<script setup>
import { ref, computed } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import AppLayout from '@/Layouts/AppLayout.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import SecondaryButton from '@/Components/SecondaryButton.vue'
import TextInput from '@/Components/TextInput.vue'
import PaginationLinks from '@/Components/PaginationLinks.vue'
import DialogModal from '@/Components/DialogModal.vue'

const props = defineProps({
  codes: Object,
  pageSearchQuery: String,
  showInactive: {
    type: Boolean,
    default: false,
  },
})

const searchQuery = ref(props.pageSearchQuery || '')
const deletingItem = ref(null)

const openDeleteModal = (item) => deletingItem.value = item
const closeDeleteModal = () => deletingItem.value = null

const title = computed(() =>
  props.showInactive ? 'Deactivated Pricing' : 'Type and Global Pricing'
)

const makeSearchRequest = _.debounce(() => {
  const routeName = props.showInactive
    ? 'admin.pricing.inactive'
    : 'admin.pricing.index'

  Inertia.get(route(routeName), { query: searchQuery.value }, { preserveScroll: true })
}, 300)

const goToActive = () => {
  Inertia.get(route('admin.pricing.index'), {}, { preserveScroll: true })
}
const goToInactive = () => {
  Inertia.get(route('admin.pricing.inactive'), {}, { preserveScroll: true })
}

const deleteItem = (id) => {
  Inertia.delete(route('admin.pricing.destroy', id), { preserveScroll: true })
  deletingItem.value = null
}

// 👉 nuevo: activar un item desactivado
const activateItem = (id) => {
  Inertia.post(route('admin.pricing.activate', id), {}, { preserveScroll: true })
}
</script>

<template>
  <AppLayout :title="title">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        {{ title }}
      </h2>
    </template>

    <div class="mx-auto py-10 sm:px-6 lg:px-8">
      <div class="mb-4 flex justify-between items-center gap-4">
        <TextInput
          v-model="searchQuery"
          placeholder="Search by item id or description..."
          @input="makeSearchRequest"
          class="w-1/2"
        />

        <div class="flex items-center gap-2">
          <SecondaryButton v-if="showInactive" @click="goToActive">
            View Active Pricing
          </SecondaryButton>
          <SecondaryButton v-else @click="goToInactive">
            View Deactivated Pricing
          </SecondaryButton>

          <a :href="route('admin.pricing.create')">
            <PrimaryButton>Add Pricing Item</PrimaryButton>
          </a>
        </div>
      </div>

      <table class="min-w-full w-full text-left text-sm">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-2">Item ID</th>
            <th class="px-4 py-2">Description</th>
            <th class="px-4 py-2">Price per Hour</th>
            <th class="px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in codes.data" :key="c.ID" class="border-b">
            <td class="px-4 py-2">{{ c.itemid }}</td>
            <td class="px-4 py-2">{{ c.description }}</td>
            <td class="px-4 py-2">{{ Number(c.price).toFixed(2) }}</td>
            <td class="px-4 py-2 space-x-3">
              <a :href="route('admin.pricing.show', c.ID)" class="fa fa-eye" title="View" />
              <a :href="route('admin.pricing.edit', c.ID)" class="fa fa-pen" title="Edit" />

              <!-- Activos: toggle-on + modal para desactivar -->
              <button
                v-if="!showInactive"
                @click="openDeleteModal(c)"
                class="fa cursor-pointer transition-colors fa-toggle-on text-green-600 hover:text-green-800"
                :title="`Deactivate ${c.description}`"
              />

              <!-- Inactivos: toggle-off directo para activar -->
              <button
                v-else
                @click="activateItem(c.ID)"
                class="fa cursor-pointer transition-colors fa-toggle-off text-gray-500 hover:text-green-600"
                :title="`Activate ${c.description}`"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="mt-4">
        <PaginationLinks :pagination="codes" />
      </div>
    </div>

    <!-- Modal solo para desactivar (activos) -->
    <DialogModal :show="deletingItem !== null" @close="closeDeleteModal">
      <template #title><b>Please confirm</b></template>
      <template #content>
        You are about to deactivate pricing item
        <b v-text="deletingItem?.description"></b>.
        <br>Are you sure?
      </template>
      <template #footer>
        <SecondaryButton @click="closeDeleteModal" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="deleteItem(deletingItem.ID)">
          Confirm</PrimaryButton>
      </template>
    </DialogModal>
  </AppLayout>
</template>